import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import styles from './Modal.module.css';
import { createAudiencia } from '../../../../../services/audienciaService';
import { Audiencia } from '../../../../../interfaces/audiencia.types';

interface AddAudienciaModalProps {
    onClose: () => void;
    onAudienciaAdded: (newAudiencia: Audiencia) => void;
    caseId: number;
}

export const AddAudienciaModal: React.FC<AddAudienciaModalProps> = ({ onClose, onAudienciaAdded, caseId }) => {
    const [fecha, setFecha] = useState<string>(new Date().toISOString().split('T')[0]);
    const [hora, setHora] = useState<string>('09:00');
    const [descripcion, setDescripcion] = useState<string>('');
    const [lugarOEnlace, setLugarOEnlace] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!fecha || !hora || !descripcion || !lugarOEnlace) {
            setError('Todos los campos son obligatorios.');
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const fecha_hora = `${fecha}T${hora}:00`;
            const audienciaData = {
                caso_id: caseId,
                fecha_hora,
                descripcion,
                lugar_o_enlace: lugarOEnlace,
            };
            const newAudiencia = await createAudiencia(audienciaData);
            onAudienciaAdded(newAudiencia);
            onClose();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const modalVariants = {
        hidden: { y: 50, opacity: 0 },
        visible: { y: 0, opacity: 1 },
        exit: { y: 50, opacity: 0 }
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <motion.div 
                className={styles.modalContent}
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                onClick={(e) => e.stopPropagation()}
            >
                <header className={styles.modalHeader}>
                    <h2>Agendar Nueva Diligencia</h2>
                    <button onClick={onClose} className={styles.closeButton}><X size={24} /></button>
                </header>
                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                            <label htmlFor="fecha">Fecha*</label>
                            <input type="date" id="fecha" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="hora">Hora*</label>
                            <input type="time" id="hora" value={hora} onChange={(e) => setHora(e.target.value)} required />
                        </div>
                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                            <label htmlFor="descripcion">Descripción de la Diligencia*</label>
                            <input type="text" id="descripcion" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} placeholder="Ej: Audiencia de conciliación" required />
                        </div>
                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                            <label htmlFor="lugar_o_enlace">Lugar o Enlace Virtual*</label>
                            <input type="text" id="lugar_o_enlace" value={lugarOEnlace} onChange={(e) => setLugarOEnlace(e.target.value)} placeholder="Ej: Sala 5 del Juzgado o https://meet.google.com/..." required />
                        </div>
                    </div>
                    {error && <p className={styles.errorMessage}>{error}</p>}
                    <footer className={styles.modalFooter}>
                        <button type="button" className={styles.cancelButton} onClick={onClose} disabled={isLoading}>Cancelar</button>
                        <button type="submit" className={styles.submitButton} disabled={isLoading}>
                            {isLoading ? 'Agendando...' : 'Agendar Diligencia'}
                        </button>
                    </footer>
                </form>
            </motion.div>
        </div>
    );
};